// Choosing from a list without the keyboard popping up.
// On phones (touch) a list opens as a bottom sheet; the search box is there but only takes focus when tapped.
// On desktop the same list opens as a dialog with the search box focused.
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, ButtonBase, Chip, Dialog, Drawer, IconButton, InputAdornment, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';

type Lang = 'ar' | 'en';
const T = (lang: Lang, ar: string, en: string) => (lang === 'en' ? en : ar);

export type PickOpt = { value: string; label: string; search?: string; ar?: string; name?: string; icon?: ReactNode; hint?: string };

/** Touch screens and narrow windows get sheets instead of dropdowns. */
export function useTouchUI(): boolean {
  return useMediaQuery('(pointer: coarse), (max-width: 599.95px)', { noSsr: true });
}

/** Lists this long get a search box. */
export const PICKER_SEARCH_FROM = 9;

export interface PickerSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  lang: Lang;
  options: PickOpt[];
  /** letter of each option when the list is grouped A–Z */
  groupOf?: (o: PickOpt) => string;
  matches: (o: PickOpt, q: string) => boolean;
  multiple?: boolean;
  value: string[];
  onChange: (v: string[]) => void;
}

export function PickerSheet(p: PickerSheetProps) {
  const touch = useTouchUI();
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<string[]>(p.value);
  useEffect(() => {
    if (p.open) {
      setQ('');
      setDraft(p.value);
    }
  }, [p.open]); // eslint-disable-line react-hooks/exhaustive-deps
  const searchable = p.options.length >= PICKER_SEARCH_FROM;
  const shown = useMemo(() => (q ? p.options.filter((o) => p.matches(o, q)) : p.options), [p.options, q, p.matches]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (v: string) => {
    if (p.multiple) setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]));
    else {
      p.onChange([v]);
      p.onClose();
    }
  };
  const done = () => {
    p.onChange(draft);
    p.onClose();
  };

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      {touch && <Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1 }} />}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: touch ? 1 : 2, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>{p.title}</Typography>
        {p.multiple && draft.length > 0 && (
          <Button size="small" color="inherit" onClick={() => setDraft([])}>{T(p.lang, 'مسح', 'Clear')}</Button>
        )}
        <IconButton aria-label={T(p.lang, 'إغلاق', 'Close')} onClick={p.onClose} edge="end"><CloseIcon /></IconButton>
      </Stack>
      {searchable && (
        <Box sx={{ px: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={T(p.lang, 'ابحث…', 'Search…')}
            autoFocus={!touch}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }, htmlInput: { enterKeyHint: 'search' } }}
          />
        </Box>
      )}
      <Box role="listbox" aria-multiselectable={p.multiple || undefined} sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: 1, pb: 1, overscrollBehavior: 'contain' }}>
        {!shown.length && <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>{T(p.lang, 'لا توجد نتائج', 'No results')}</Typography>}
        {shown.map((o, i) => {
          const on = (p.multiple ? draft : p.value).includes(o.value);
          const g = p.groupOf && !q ? p.groupOf(o) : null;
          const newGroup = g !== null && (i === 0 || p.groupOf!(shown[i - 1]!) !== g);
          return (
            <Fragment key={o.value}>
              {newGroup && (
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 1.5, pb: 0.5 }}>{g}</Typography>
              )}
              <ButtonBase
                role="option"
                aria-selected={on}
                onClick={() => pick(o.value)}
                sx={(t) => ({
                  width: '100%',
                  justifyContent: 'flex-start',
                  textAlign: 'start',
                  gap: 1.5,
                  minHeight: 52,
                  px: 1.5,
                  borderRadius: 2,
                  bgcolor: on ? alpha(t.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': { bgcolor: on ? alpha(t.palette.primary.main, 0.14) : 'action.hover' },
                })}
              >
                {o.icon}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: on ? 600 : 400, color: on ? 'primary.main' : 'text.primary', overflowWrap: 'anywhere' }}>{o.label}</Typography>
                  {o.hint && <Typography variant="caption" color="text.secondary">{o.hint}</Typography>}
                </Box>
                {p.multiple ? (
                  <Box sx={{ width: 22, height: 22, borderRadius: '6px', border: 2, borderColor: on ? 'primary.main' : 'text.disabled', bgcolor: on ? 'primary.main' : 'transparent', color: 'primary.contrastText', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    {on && <CheckIcon sx={{ fontSize: 16 }} />}
                  </Box>
                ) : (
                  on && <CheckIcon color="primary" />
                )}
              </ButtonBase>
            </Fragment>
          );
        })}
      </Box>
      {p.multiple && (
        <Box sx={{ p: 2, pt: 1.5, borderTop: 1, borderColor: 'divider', pb: touch ? 'calc(16px + env(safe-area-inset-bottom))' : 2 }}>
          <Button fullWidth size="large" variant="contained" onClick={done}>
            {draft.length ? T(p.lang, `تم (${draft.length})`, `Done (${draft.length})`) : T(p.lang, 'تم', 'Done')}
          </Button>
        </Box>
      )}
    </Box>
  );

  return touch ? (
    <Drawer
      anchor="bottom"
      open={p.open}
      onClose={p.onClose}
      slotProps={{ paper: { sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '88dvh', height: p.options.length > 8 ? '88dvh' : 'auto', display: 'flex', flexDirection: 'column', pb: p.multiple ? 0 : 'env(safe-area-inset-bottom)' } } }}
    >
      {body}
    </Drawer>
  ) : (
    <Dialog open={p.open} onClose={p.onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { height: p.options.length > 8 ? '80vh' : 'auto', display: 'flex', flexDirection: 'column', borderRadius: 4 } } }}>
      {body}
    </Dialog>
  );
}

/** A field that looks like a dropdown but opens the sheet and never raises the keyboard. */
export function PickerField(p: {
  id: string;
  lang: Lang;
  label: string;
  title?: string;
  options: PickOpt[];
  value: string;
  onChange: (v: string | undefined) => void;
  matches: (o: PickOpt, q: string) => boolean;
  groupOf?: (o: PickOpt) => string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  /** text shown in the box instead of the option label (e.g. flag + code) */
  display?: (o: PickOpt) => string;
  sx?: object;
}) {
  const [open, setOpen] = useState(false);
  const selected = p.options.find((o) => o.value === p.value);
  return (
    <>
      <TextField
        id={p.id}
        fullWidth
        label={p.label}
        value={selected ? (p.display?.(selected) ?? selected.label) : ''}
        error={p.error}
        helperText={p.helperText}
        disabled={p.disabled}
        onClick={() => !p.disabled && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        sx={{ '& input': { cursor: 'pointer', caretColor: 'transparent' }, ...p.sx }}
        slotProps={{
          htmlInput: { readOnly: true, inputMode: 'none', 'aria-haspopup': 'listbox' },
          input: { endAdornment: <InputAdornment position="end"><ExpandMoreIcon sx={{ color: 'action.active' }} /></InputAdornment> },
        }}
      />
      <PickerSheet
        open={open}
        onClose={() => setOpen(false)}
        title={p.title ?? p.label}
        lang={p.lang}
        options={p.options}
        groupOf={p.groupOf}
        matches={p.matches}
        value={p.value ? [p.value] : []}
        onChange={(v) => p.onChange(v[0])}
      />
    </>
  );
}

/** Many-choice answer (e.g. 30+ features): the chosen ones as chips, the full list in a sheet. */
export function MultiPicker(p: {
  lang: Lang;
  title: string;
  options: PickOpt[];
  value: string[];
  onChange: (v: string[] | undefined) => void;
  matches: (o: PickOpt, q: string) => boolean;
  groupOf?: (o: PickOpt) => string;
  error?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const chosen = p.options.filter((o) => p.value.includes(o.value));
  return (
    <>
      {chosen.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {chosen.map((o) => (
            <Chip key={o.value} label={o.label} color="primary" variant="outlined" disabled={p.disabled} onDelete={() => { const next = p.value.filter((x) => x !== o.value); p.onChange(next.length ? next : undefined); }} />
          ))}
        </Stack>
      )}
      <Button
        variant="outlined"
        color={p.error ? 'error' : 'primary'}
        disabled={p.disabled}
        onClick={() => setOpen(true)}
        endIcon={<ExpandMoreIcon />}
        sx={{ width: '100%', justifyContent: 'space-between', minHeight: 52, borderRadius: 3 }}
      >
        {chosen.length
          ? T(p.lang, `تعديل الاختيار (${chosen.length} من ${p.options.length})`, `Change selection (${chosen.length} of ${p.options.length})`)
          : T(p.lang, `اختر من القائمة (${p.options.length})`, `Choose from the list (${p.options.length})`)}
      </Button>
      <PickerSheet
        open={open}
        onClose={() => setOpen(false)}
        title={p.title}
        lang={p.lang}
        options={p.options}
        groupOf={p.groupOf}
        matches={p.matches}
        multiple
        value={p.value}
        onChange={(v) => p.onChange(v.length ? v : undefined)}
      />
    </>
  );
}
